import Box from "@ui-kit/layout/Box/Box.web";
import Skeleton from "@ui-kit/feedback/Skeleton/Skeleton.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { componentName } from "@shared/utils/bem";
import {
  TicketTypeSerializer,
  PublicBaseEventSerializer,
} from "@shared/modules/events/types/public";
import usePublicEventService from "@app-contexts/public/features/event-detail/data/services/usePublicEventsService";
import { useMemo } from "react";
import { AttractionItem } from "@app-contexts/dashboard/features/events/data/types/events";
import usePublicVenueService from "@app-contexts/public/features/org-profile/data/services/usePublicVenueService";
import { PublicVenueSerializer } from "@app-contexts/dashboard/features/venues/data/types/venues";
import Card from "@shared/components/ui-kit/data-display/Card/Card.web";
import TicketTypesContainer from "@app-contexts/public/features/tickets/components/TicketTypesContainer/TicketTypesContainer.web";
import "./AttractionTicketTypes.css";
import InventoryBadge from "@shared/components/application/data-display/badges/InventoryBadge/InventoryBadge.web";
import ReadMore from "@shared/components/ui-kit/typography/ReadMore/ReadMore.web";
import { useTranslation } from "@shared/utils/internationalization";
import {
  TicketTypeDetailModal,
  TicketTypeImage,
} from "./TicketTypeDetailModal";

export interface AttractionTicketTypesProps {
  attractionItem?: AttractionItem;
  attractionEvent?: PublicBaseEventSerializer;
}

export const TicketTypeCard = ({
  ticketType,
  event,
  venue,
}: {
  ticketType: TicketTypeSerializer;
  event?: PublicBaseEventSerializer;
  venue?: PublicVenueSerializer;
}) => {
  const bem = componentName("ticket-type-card").modifiers({
    isSoldOut: ticketType.sold_out,
  });
  const detailsModal = event && venue && (
    <TicketTypeDetailModal
      ticketType={ticketType}
      event={event}
      venue={venue}
    />
  );

  // TODO: deal with ticket authorization
  return (
    <Box className={bem.className()}>
      <Box className={bem.element("inner-container")}>
        <Box className={bem.element("logo")}>
          <TicketTypeImage ticketType={ticketType} event={event} />
        </Box>

        {event && venue && (
          <InventoryBadge
            className={bem.element("inventory-badge")}
            tt={ticketType}
            eventData={event}
            venueData={venue}
          />
        )}

        <Box className={bem.element("details")}>
          {event && venue && (
            <TicketTypesContainer
              eventData={event}
              venue={venue}
              tt={ticketType}
              isLoading={false}
              isRecurring={false}
              ticketsAuthorizedByPassword={[]}
              ticketAuthArray={[]}
              // eslint-disable-next-line @typescript-eslint/no-empty-function
              updateTicketAuthArray={() => {}}
              basketVoucherCode={null}
              userVouchers={[]}
              quantityButtonsAlwaysBelow
              hideDescription
              titleAccessory={detailsModal}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export const AttractionTicketTypes = ({
  attractionItem,
  attractionEvent,
}: AttractionTicketTypesProps) => {
  const { t } = useTranslation("common");
  const bem = componentName("attraction-ticket-types");
  const { useRetrieve } = usePublicEventService();
  const { data: ticketTypeEvent, isLoading } = useRetrieve(
    attractionItem?.ids?.[0]
  );

  const { useRetrieve: useRetrieveVenue } = usePublicVenueService();
  const { data: venue } = useRetrieveVenue(attractionEvent?.venue?.id);

  const filteredTicketTypes = useMemo(() => {
    return ticketTypeEvent?.ticket_types?.filter(
      (tt) =>
        !attractionItem?.item_ids?.length ||
        attractionItem?.item_ids.includes(tt.id)
    );
  }, [ticketTypeEvent?.ticket_types, attractionItem?.item_ids]);

  const soldOutTicketTypes = useMemo(() => {
    return filteredTicketTypes?.filter((tt) => tt.sold_out) ?? [];
  }, [filteredTicketTypes]);

  const availableTicketTypes = useMemo(() => {
    return filteredTicketTypes?.filter((tt) => !tt.sold_out) ?? [];
  }, [filteredTicketTypes]);

  return (
    <Card
      flexDirection="column"
      w="full"
      rounded={{ base: 0, sm: "lg" }}
      className={bem.className()}
    >
      <Box className={bem.element("title")}>
        <Text className={bem.element("title")}>{attractionItem?.title}</Text>
      </Box>

      {attractionItem?.description && (
        <Box className={bem.element("description")}>
          <ReadMore
            openReadMoreText={t("Read More")}
            closeReadMoreText={t("Show Less")}
            descriptionHtml={attractionItem.description}
          />
        </Box>
      )}

      <Box className={bem.element("ticket-types-container")}>
        {isLoading &&
          Array.from({ length: 4 }).map((_, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Skeleton key={index} minHeight="200px" minWidth="element.7xl" />
          ))}

        {!isLoading &&
          // put sold out ticket types at the end
          [...availableTicketTypes, ...soldOutTicketTypes]?.map(
            (ticketType) => (
              <TicketTypeCard
                ticketType={ticketType}
                key={ticketType.id}
                venue={venue}
                event={ticketTypeEvent}
              />
            )
          )}
      </Box>
    </Card>
  );
};
